import { computed, watch } from 'vue';
import { useRoute } from '#imports';
import { useMainStore } from '../stores/mainStore.js';

export function useSearchFilters() {
  const mainStore = useMainStore();
  const schoolsState = useState('schoolsState', () => []);
  const typesState = useState('typesState', () => []);
  const dateState = useState('dateState', () => []);

  const route = useRoute();

  /** Normalize Vue Router query values (scalar or single-element array) to a single value. */
  const toSingle = (val) => {
    if (val == null) return undefined;
    return Array.isArray(val) ? val[0] : val;
  };

  const initializeSchools = (organizations) => {
    const secondaryOrgs = route.query?.secondary_organizations;
    const secondaryOrgsArray = Array.isArray(secondaryOrgs) ? secondaryOrgs : secondaryOrgs ? [secondaryOrgs] : [];
    schoolsState.value = organizations.map((school) => ({
      name: school.name,
      org_id: school.org_id,
      checked:
        secondaryOrgsArray.length > 0 ? secondaryOrgsArray.includes(String(school.org_id)) : (school.checked ?? false),
    }));
  };

  const initializeTypes = (typeOptions) => {
    const queryIndices = route.query?.query_indices;
    const queryIndicesArray = Array.isArray(queryIndices) ? queryIndices : queryIndices ? [queryIndices] : [];

    typesState.value = typeOptions.map((type) => ({
      ...type,
      checked: queryIndicesArray.includes(type.type),
      filterValue: type.filterValue,
    }));
  };

  const initializeDateOptions = (dateOptions) => {
    const queryStartDate = toSingle(route.query?.start_date);
    const queryEndDate = toSingle(route.query?.end_date);
    dateState.value = dateOptions.map((date) => ({
      name: date.name,
      idx: date.idx,
      checked:
        queryStartDate && queryEndDate
          ? queryStartDate === date.start_date && queryEndDate === date.end_date
          : (date.checked ?? false),
      start_date: date.start_date,
      end_date: date.end_date,
      filterValue: date.filterValue,
    }));
  };

  watch(
    () => route.query,
    (newQuery) => {
      const secondaryOrgs = newQuery?.secondary_organizations;
      const secondaryOrgsArray = Array.isArray(secondaryOrgs) ? secondaryOrgs : secondaryOrgs ? [secondaryOrgs] : [];

      // Update schools state
      const defaultChecked = !!mainStore.settings?.searchDefaultAllOrgs;
      schoolsState.value.forEach((school) => {
        school.checked =
          secondaryOrgsArray.length > 0 ? secondaryOrgsArray.includes(String(school.org_id)) : defaultChecked;
      });

      const queryIndices = newQuery?.query_indices;
      const queryIndicesArray = Array.isArray(queryIndices) ? queryIndices : queryIndices ? [queryIndices] : [];

      typesState.value.forEach((type) => {
        type.checked = queryIndicesArray.includes(type.type);
      });

      const queryStartDate = toSingle(newQuery?.start_date);
      const queryEndDate = toSingle(newQuery?.end_date);
      dateState.value.forEach((date) => {
        date.checked =
          queryStartDate && queryEndDate ? queryStartDate === date.start_date && queryEndDate === date.end_date : false;
      });
    },
    { immediate: true, deep: true },
  );

  const selectedSchools = computed(() =>
    schoolsState.value.filter((school) => school.checked).map((school) => school.org_id),
  );

  const selectedTypes = computed(() => typesState.value.filter((type) => type.checked).map((type) => type.type));

  const selectedDate = computed(() => dateState.value.find((date) => date.checked));

  const hasActiveFilters = computed(() => {
    const schoolsActive = selectedSchools.value.length > 0;
    const typesActive = selectedTypes.value.length > 0;
    const dateActive = !!selectedDate.value;

    return schoolsActive || typesActive || dateActive;
  });

  const clearAllFilters = () => {
    schoolsState.value.forEach((school) => (school.checked = false));
    typesState.value.forEach((type) => (type.checked = false));
    dateState.value.forEach((date) => (date.checked = false));
  };

  return {
    schoolsState,
    typesState,
    dateState,

    initializeSchools,
    initializeTypes,
    initializeDateOptions,

    selectedSchools,
    selectedTypes,
    selectedDate,
    hasActiveFilters,

    clearAllFilters,
  };
}
